import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import ResizableImage from '../components/ResizableImageExtension';
import EditorToolbar from '../components/EditorToolbar';
import api from '../utils/api';
import { Megaphone, Plus, Pin, PinOff, X } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LottieAnim from '../components/LottieAnim';

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

function NoticeEditor({ onSave, onCancel }) {
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState('');
  const [isPinned, setIsPinned] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      ResizableImage,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: '공지 내용을 입력하세요...' }),
    ],
    editorProps: {
      attributes: { class: 'tiptap !min-h-[200px] !p-3' },
      transformPastedHTML: (html) => html.replace(/ style="[^"]*"/gi, (match) => {
        // --tw-* CSS 변수만 있는 style 속성은 제거, 실제 CSS 속성(color, font-weight 등)은 유지
        const cleaned = match.replace(/--tw-[^;]+;\s*/g, '').replace(/style="\s*"/, '');
        return cleaned;
      }),
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(
                view.state.schema.nodes.image.create({ src: url })
              ));
            })();
            return true;
          }
        }
        return false;
      },
    },
  });

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const save = () => {
    if (!title.trim()) return;
    const content = editor?.getHTML() || '';
    onSave({ title: title.trim(), content, isPinned });
  };

  return (
    <div className="bg-gray-900 border border-brand/30 rounded-xl mb-4">
      <div className="px-5 pt-4 pb-2">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="제목을 입력하세요"
          autoFocus
          onKeyDown={e => e.key === 'Enter' && editor?.commands.focus()}
          className="w-full bg-transparent text-white text-base font-semibold focus:outline-none placeholder-gray-600 border-b border-gray-800 pb-2 mb-3"
        />
        <EditorToolbar editor={editor} onImageUpload={() => fileInputRef.current?.click()} />
        <div className="border border-gray-800 rounded-lg mt-2 overflow-hidden">
          <EditorContent editor={editor} />
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />
      </div>
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-800">
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
          <input type="checkbox" checked={isPinned} onChange={e => setIsPinned(e.target.checked)} className="accent-brand" />
          상단 고정
        </label>
        <div className="flex gap-2">
          <button onClick={onCancel} className="px-4 py-1.5 text-gray-400 text-sm hover:text-white">취소</button>
          <button onClick={save} className="px-4 py-1.5 bg-brand text-white text-sm rounded-lg hover:bg-brand/80">저장</button>
        </div>
      </div>
    </div>
  );
}

export default function NoticesPage() {
  const [notices, setNotices] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('mfg_user') || '{}');
  const isSuperadmin = user.role === 'superadmin';
  const LIMIT = 20;

  const load = async (p = page) => {
    const r = await api.get('/notices', { params: { page: p, limit: LIMIT } });
    setNotices(r.data.notices);
    setTotal(r.data.total);
  };

  useEffect(() => { load(page); }, [page]);

  const save = async ({ title, content, isPinned }) => {
    await api.post('/notices', { title, content, isPinned });
    setShowForm(false);
    load(1);
    setPage(1);
  };

  const togglePin = async (e, n) => {
    e.stopPropagation();
    await api.put(`/notices/${n.id}`, { isPinned: !n.isPinned });
    load(page);
  };

  const fmt = (d) => new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      {/* 헤더 */}
      <PageHeader icon={Megaphone} title="공지사항" count={total}>
        {isSuperadmin && (
          <button onClick={() => setShowForm(s => !s)}
            className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg transition-colors ${
              showForm ? 'bg-gray-700 text-gray-300' : 'bg-brand text-white hover:bg-brand/80'
            }`}>
            {showForm ? <><X size={14} /> 취소</> : <><Plus size={14} /> 작성</>}
          </button>
        )}
      </PageHeader>

      {/* 인라인 작성 에디터 */}
      {showForm && (
        <NoticeEditor onSave={save} onCancel={() => setShowForm(false)} />
      )}

      {/* 공지 목록 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        {/* 테이블 헤더 */}
        <div className="hidden sm:grid grid-cols-[3rem_1fr_7rem_7rem] gap-2 px-4 py-2 border-b border-gray-800 text-gray-600 text-xs">
          <span className="text-center">번호</span>
          <span>제목</span>
          <span>최근수정자</span>
          <span className="text-right">최근수정일</span>
        </div>

        {notices.length === 0 ? (
          <div className="flex flex-col items-center py-10">
            <LottieAnim type="empty" size={100} />
            <p className="text-gray-600 text-sm mt-2">공지사항이 없습니다.</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-800">
            {notices.map((n, idx) => (
              <li key={n.id}
                onClick={() => navigate(`/notices/${n.id}`)}
                className="grid grid-cols-[3rem_1fr] sm:grid-cols-[3rem_1fr_7rem_7rem] gap-2 items-center px-4 py-3.5 cursor-pointer hover:bg-gray-800/50 transition-colors group">
                {/* 번호 / 핀 */}
                <div className="relative flex items-center justify-center">
                  {isSuperadmin ? (
                    <>
                      <span className={`text-xs group-hover:opacity-0 transition-opacity ${n.isPinned ? 'text-brand text-[10px] font-bold' : 'text-gray-600'}`}>
                        {n.isPinned ? '공지' : total - (page - 1) * LIMIT - idx}
                      </span>
                      <button
                        onClick={e => togglePin(e, n)}
                        title={n.isPinned ? '고정 해제' : '상단 고정'}
                        className={`absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded ${
                          n.isPinned ? 'text-brand' : 'text-gray-600 hover:text-gray-400'
                        }`}
                      >
                        {n.isPinned ? <Pin size={12} /> : <PinOff size={12} />}
                      </button>
                    </>
                  ) : (
                    <span className={`text-xs text-center ${n.isPinned ? 'text-brand text-[10px] font-bold' : 'text-gray-600'}`}>
                      {n.isPinned ? '공지' : total - (page - 1) * LIMIT - idx}
                    </span>
                  )}
                </div>

                {/* 제목 */}
                <div className="min-w-0">
                  <p className={`text-sm font-medium truncate ${n.isPinned ? 'text-white' : 'text-gray-200'}`}>{n.title}</p>
                  <p className="sm:hidden text-xs text-gray-600 mt-0.5">
                    {n.lastEditor?.name || n.author?.name || n.author?.username} · {fmt(n.updatedAt || n.createdAt)}
                  </p>
                </div>

                {/* 최근수정자 */}
                <span className="hidden sm:block text-xs text-gray-500 truncate">
                  {n.lastEditor?.name || n.author?.name || n.author?.username}
                </span>

                {/* 최근수정일 */}
                <span className="hidden sm:block text-xs text-gray-500 text-right">
                  {fmt(n.updatedAt || n.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-1 mt-4">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)}
              className={`w-8 h-8 text-sm rounded-md ${
                p === page ? 'bg-brand text-white' : 'text-gray-500 hover:text-white hover:bg-gray-800'
              }`}>
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
